import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../hooks/useScrollReveal';

export interface Benefit {
  icon: string;
  title: string;
  description: string;
}

export interface WhyChooseUsProps {
  eyebrow: string;
  headline: string;
  image: string;
  imageAlt: string;
  benefits: Benefit[];
  ctaText: string;
  ctaLink: string;
}

export default function WhyChooseUs({
  eyebrow,
  headline,
  image,
  imageAlt,
  benefits,
  ctaText,
  ctaLink,
}: WhyChooseUsProps) {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-white overflow-hidden">
      <div className="flex flex-col lg:flex-row min-h-[680px]">
        {/* Image panel */}
        <div className="w-full lg:w-[52%] flex-shrink-0 h-72 lg:h-auto relative overflow-hidden">
          <img
            src={image}
            alt={imageAlt}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent lg:to-white/8" />
          {/* Gold accent strip on right edge */}
          <div className="hidden lg:block absolute right-0 top-20 bottom-20 w-[2px] bg-gradient-to-b from-transparent via-gold/55 to-transparent" />
          {/* Bottom-left stats overlay */}
          <div className="absolute bottom-7 left-7 hidden lg:flex items-center gap-6">
            <div className="bg-charcoal/85 backdrop-blur-sm border border-white/8 px-5 py-3.5">
              <p className="font-serif text-gold text-2xl font-light leading-none mb-1">70%+</p>
              <p className="text-white/40 text-[9px] tracking-wider uppercase">Energy Savings</p>
            </div>
            <div className="bg-charcoal/85 backdrop-blur-sm border border-white/8 px-5 py-3.5">
              <p className="font-serif text-gold text-2xl font-light leading-none mb-1">10+</p>
              <p className="text-white/40 text-[9px] tracking-wider uppercase">App Types</p>
            </div>
          </div>
        </div>

        {/* Text panel */}
        <div
          ref={ref}
          className={`w-full lg:w-[48%] flex items-center px-8 lg:px-14 xl:px-20 py-16 lg:py-24 transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}
        >
          <div className="max-w-md w-full">
            <div className="flex items-center gap-4 mb-6">
              <span className="block w-8 h-px bg-gold flex-shrink-0" />
              <span className="text-gold text-[9px] font-semibold tracking-[0.38em] uppercase">
                {eyebrow}
              </span>
            </div>

            <h2
              className="font-serif text-charcoal leading-tight mb-12"
              style={{ fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', fontWeight: 300 }}
            >
              {headline}
            </h2>

            <div className="space-y-8">
              {benefits.map((benefit, i) => (
                <BenefitRow key={benefit.title} benefit={benefit} index={i} />
              ))}
            </div>

            <div className="mt-12 pt-10 border-t border-gray-100">
              <Link
                to={ctaLink}
                className="whitespace-nowrap inline-flex items-center gap-3 bg-charcoal text-white text-[10px] font-bold tracking-[0.25em] uppercase px-10 py-4 hover:bg-gold hover:text-charcoal transition-all duration-300 cursor-pointer group"
              >
                {ctaText}
                <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BenefitRow({ benefit, index }: { benefit: Benefit; index: number }) {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`flex items-start gap-5 transition-all duration-600 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{ transitionDelay: `${index * 90}ms` }}
    >
      {/* Icon block */}
      <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center border border-gold/30 bg-gold/[0.05] group-hover:border-gold transition-all duration-300">
        <i className={`${benefit.icon} text-gold text-lg`}></i>
      </div>
      <div className="pt-1">
        <h3 className="text-charcoal font-semibold text-sm mb-2 tracking-wide leading-snug">
          {benefit.title}
        </h3>
        <p className="text-stone text-sm leading-[1.85]">{benefit.description}</p>
      </div>
    </div>
  );
}
